"use client";

import { useAuth } from "@/contexts/AuthContext";
import { db } from "@/lib/firebase";
import { Team, Tournament } from "@/types";
import { useQuery } from "@tanstack/react-query";
import {
  collection,
  documentId,
  getDocs,
  query,
  where,
} from "firebase/firestore";

export interface UserTeamWithTournament {
  teamId: Team["id"];
  teamName: Team["name"];
  tournamentId: Tournament["id"];
  tournamentName: Tournament["name"];
  tournamentSlug: Tournament["slug"];
}

/**
 * Fetches all teams that the current user is a member of,
 * along with their tournament information.
 *
 * This is used for the user menu to show quick links to the user's teams.
 *
 * @returns Query result with array of user teams with tournament info
 */
export function useUserTeams() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["userTeams", user?.id],
    queryFn: async (): Promise<UserTeamWithTournament[]> => {
      if (!user?.id || !user.tournamentIds?.length) return [];

      // Batch tournament IDs in groups of 10 (Firestore "in" limit)
      const tournamentBatches: string[][] = [];
      for (let i = 0; i < user.tournamentIds.length; i += 10) {
        tournamentBatches.push(user.tournamentIds.slice(i, i + 10));
      }

      // Fetch teams for all tournaments where user is a member
      const teamsPromises = tournamentBatches.map(async (batch) => {
        const teamsQuery = query(
          collection(db, "teams"),
          where("tournamentId", "in", batch),
          where("playerIds", "array-contains", user.id),
          where("isActive", "==", true),
        );
        return getDocs(teamsQuery);
      });

      const teamsSnapshots = await Promise.all(teamsPromises);
      const teams: Array<{
        id: string;
        name: string | undefined;
        tournamentId: string;
      }> = [];

      for (const snapshot of teamsSnapshots) {
        for (const doc of snapshot.docs) {
          teams.push({
            id: doc.id,
            name: doc.get("name") as string | undefined,
            tournamentId: doc.get("tournamentId") as string,
          });
        }
      }

      if (teams.length === 0) return [];

      // Get unique tournament IDs
      const tournamentIds = [...new Set(teams.map((t) => t.tournamentId))];

      // Batch fetch tournaments
      const tournamentBatchesForFetch: string[][] = [];
      for (let i = 0; i < tournamentIds.length; i += 10) {
        tournamentBatchesForFetch.push(tournamentIds.slice(i, i + 10));
      }

      const tournamentsPromises = tournamentBatchesForFetch.map(
        async (batch) => {
          const tournamentsQuery = query(
            collection(db, "tournaments"),
            where(documentId(), "in", batch),
          );
          return getDocs(tournamentsQuery);
        },
      );

      const tournamentsSnapshots = await Promise.all(tournamentsPromises);
      const tournamentMap = new Map<
        string,
        { name: string; slug: string }
      >();

      for (const snapshot of tournamentsSnapshots) {
        for (const doc of snapshot.docs) {
          if (doc.get("archived") === true) continue;
          tournamentMap.set(doc.id, {
            name: doc.get("name") as string,
            slug: doc.get("slug") as string,
          });
        }
      }

      // Combine team and tournament data
      return teams
        .map((team) => {
          const tournament = tournamentMap.get(team.tournamentId);
          if (!tournament) return null;

          return {
            teamId: team.id,
            teamName: team.name,
            tournamentId: team.tournamentId,
            tournamentName: tournament.name,
            tournamentSlug: tournament.slug,
          };
        })
        .filter((item): item is UserTeamWithTournament => item !== null);
    },
    enabled: !!user?.id && !!user.tournamentIds?.length,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 1,
  });
}
